// @shared/stores/useRoomsStore.js
import { create } from "zustand";
import {
  createAndSetRoomRTDB,
  getLobbyRooms,
  joinRoomRTDB,
  subscribeToRoomRTDB,
} from "../utils/roomFunctions";
import { db } from "../firebase/config";
import {
  collection,
  onSnapshot,
  orderBy,
  query,
} from "firebase/firestore";
import {
  getDatabase,
  limitToLast,
  onValue,
  orderByChild,
  query as rtdbQuery,
  ref,
} from "firebase/database";
import AudioManager from "../utils/AudioManager";
import { buildLegacyMatchDataFromV2 } from "../game/rtdbSchema";

const persistRoomId = (roomId) => {
  try {
    if (roomId) {
      localStorage.setItem("roomId", roomId);
    }
  } catch {}
};

const clearPersistedRoomId = () => {
  try {
    localStorage.removeItem("roomId");
  } catch {}
};

const deriveMatchData = (state) =>
  buildLegacyMatchDataFromV2({
    summary: state.matchSummary,
    playersById: state.matchPlayersById,
    recentMovesNode: state.matchRecentMovesById,
    legacyFallback: state.matchLegacyData,
  });

const applyMatchState = (partial) => (state) => {
  const nextState = { ...state, ...partial };
  return {
    ...partial,
    matchData: deriveMatchData(nextState),
  };
};

export const useRoomsStore = create((set, get) => ({
  currentRoom: null,
  matchData: null,
  matchLegacyData: null,
  matchSummary: null,
  matchPlayersById: {},
  matchRecentMovesById: {},
  rooms: [],
  lastRoomDoc: null,
  roomUnsub: null,
  matchUnsub: null,
  matchSummaryUnsub: null,
  matchPlayersUnsub: null,
  matchRecentMovesUnsub: null,
  matchLegacyUnsub: null,
  chatMessages: [],
  chatUnsub: null,
  hasUnreadMessages: false,
  unreadCount: 0,
  hands: {},
  decks: {},
  cardCounts: {},
  chatDrawerOpen: false,

  setHand: (playerId, cards) =>
    set((state) => ({
      hands: { ...state.hands, [playerId]: cards },
    })),

  setDeck: (playerId, cards) =>
    set((state) => ({
      decks: { ...state.decks, [playerId]: cards },
    })),

  clearPlayerPrivateData: (playerId) =>
    set((state) => {
      const hands = { ...state.hands };
      const decks = { ...state.decks };
      delete hands[playerId];
      delete decks[playerId];
      return { hands, decks };
    }),

  setCardCount: (playerId, payload) =>
    set((state) => ({
      cardCounts: { ...state.cardCounts, [playerId]: payload || null },
    })),

  clearPlayerData: () =>
    set({
      hands: {},
      decks: {},
      cardCounts: {},
    }),

  setMatchData: (data) =>
    set(() => ({
      matchLegacyData: data,
      matchData: data,
    })),

  subscribeToMatchLegacy: (roomId, matchId = "match") => {
    const dbRef = getDatabase();
    const matchRef = ref(dbRef, `rooms/${roomId}/matchData/${matchId}`);
    const unsubscribe = onValue(matchRef, (snapshot) => {
      const data = snapshot.val() || null;
      set(applyMatchState({ matchLegacyData: data }));
    });

    set({ matchLegacyUnsub: unsubscribe });
    return unsubscribe;
  },

  subscribeToMatchSummary: (roomId) => {
    const dbRef = getDatabase();
    const summaryRef = ref(dbRef, `rooms/${roomId}/matchData/summary`);
    const unsubscribe = onValue(summaryRef, (snapshot) => {
      const data = snapshot.val() || null;
      if (data?.schemaVersion === 2) {
        const legacyUnsub = get().matchLegacyUnsub;
        if (typeof legacyUnsub === "function") {
          legacyUnsub();
          set({ matchLegacyUnsub: null, matchLegacyData: null });
        }
      } else if (!get().matchLegacyUnsub) {
        get().subscribeToMatchLegacy(roomId);
      }

      set(applyMatchState({ matchSummary: data }));
    });

    set({ matchSummaryUnsub: unsubscribe });
    return unsubscribe;
  },

  subscribeToMatchPlayers: (roomId) => {
    const dbRef = getDatabase();
    const playersRef = ref(dbRef, `rooms/${roomId}/matchData/players`);
    const unsubscribe = onValue(playersRef, (snapshot) => {
      set(applyMatchState({ matchPlayersById: snapshot.val() || {} }));
    });

    set({ matchPlayersUnsub: unsubscribe });
    return unsubscribe;
  },

  subscribeToRecentMoves: (roomId) => {
    const dbRef = getDatabase();
    const recentMovesRef = rtdbQuery(
      ref(dbRef, `rooms/${roomId}/matchData/recentMoves`),
      orderByChild("seq"),
      limitToLast(16),
    );
    const unsubscribe = onValue(recentMovesRef, (snapshot) => {
      set(applyMatchState({ matchRecentMovesById: snapshot.val() || {} }));
    });

    set({ matchRecentMovesUnsub: unsubscribe });
    return unsubscribe;
  },

  subscribeToChatThread: (roomId, userId) => {
    const chatQuery = query(
      collection(db, "rooms", roomId, "chatThreadData"),
      orderBy("createdAt", "asc"),
    );

    let prevMessageCount = 0;

    const unsub = onSnapshot(chatQuery, (snapshot) => {
      const messages = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      const nonSelfMessages = messages.filter((msg) => msg.senderId !== userId);
      const unreadCount = nonSelfMessages.length;
      const latest = messages[messages.length - 1];
      if (
        latest &&
        latest.senderId !== userId &&
        messages.length > prevMessageCount &&
        !useRoomsStore.getState().chatDrawerOpen
      ) {
        AudioManager.playSFX("liveChat");
      }

      prevMessageCount = messages.length;

      set({
        chatMessages: messages,
        hasUnreadMessages: unreadCount > 0,
        unreadCount,
      });
    });

    set({ chatUnsub: unsub });
  },

  clearUnreadMessages: () => set({ hasUnreadMessages: false, unreadCount: 0 }),

  setChatDrawerOpen: (isOpen) => set({ chatDrawerOpen: isOpen }),

  unsubscribeFromChatThread: () => {
    const { chatUnsub } = get();
    if (chatUnsub) {
      chatUnsub();
      set({ chatUnsub: null, chatMessages: [] });
    }
  },

  unsubscribeFromMatch: () => {
    [
      "matchUnsub",
      "matchSummaryUnsub",
      "matchPlayersUnsub",
      "matchRecentMovesUnsub",
      "matchLegacyUnsub",
    ].forEach((key) => {
      const unsub = get()[key];
      if (typeof unsub === "function") {
        unsub();
      }
    });

    set({
      matchUnsub: null,
      matchSummaryUnsub: null,
      matchPlayersUnsub: null,
      matchRecentMovesUnsub: null,
      matchLegacyUnsub: null,
      matchData: null,
      matchLegacyData: null,
      matchSummary: null,
      matchPlayersById: {},
      matchRecentMovesById: {},
      hands: {},
      decks: {},
      cardCounts: {},
    });
  },

  createAndSetRoom: async (userId, settings, newRoomId = null) => {
    const roomId = await createAndSetRoomRTDB(userId, settings, newRoomId);
    const room = { id: roomId, ...settings };
    get().subscribeToRoom(room.id);
    get().subscribeToChatThread(room.id, userId);
    persistRoomId(roomId);
    set({ currentRoom: { id: roomId, ...settings } });
  },

  joinAndSetRoom: async (roomId, userId, playerData) => {
    const room = await joinRoomRTDB(roomId, userId, playerData);
    get().subscribeToRoom(room.id);
    get().subscribeToChatThread(room.id, userId);
    persistRoomId(room.id);
    set({ currentRoom: room });
  },

  setCurrentRoom: (roomOrUpdater) =>
    set((state) => {
      const nextRoom =
        typeof roomOrUpdater === "function"
          ? roomOrUpdater(state.currentRoom)
          : roomOrUpdater;

      persistRoomId(nextRoom?.id);

      return {
        currentRoom: nextRoom,
      };
    }),

  subscribeToRoom: (roomId) => {
    if (!roomId) return () => {};
    const prev = get().roomUnsub;
    if (typeof prev === "function") prev();

    const unsub = subscribeToRoomRTDB(roomId, (roomData) => {
      if (!roomData) {
        set({ currentRoom: null });
        return;
      }

      const { currentRoom } = get();
      if (!currentRoom || currentRoom.id === roomId) {
        persistRoomId(roomId);
        set({ currentRoom: { id: roomId, ...roomData } });
      }
    });

    set({ roomUnsub: unsub });
    return unsub;
  },

  unsubscribeFromRoom: () => {
    const { roomUnsub } = get();
    if (roomUnsub) {
      roomUnsub();
      set({ roomUnsub: null });
    }
  },

  clearRoom: () => {
    get().unsubscribeFromRoom();
    get().unsubscribeFromMatch();
    get().unsubscribeFromChatThread();
    clearPersistedRoomId();
    set({ currentRoom: null });
  },

  fetchLobbyRooms: async () => {
    const { rooms, lastDoc } = await getLobbyRooms();
    set({ rooms, lastRoomDoc: lastDoc });
  },

  fetchMoreLobbyRooms: async () => {
    const { lastRoomDoc } = get();
    if (!lastRoomDoc) return;

    const { rooms: newRooms, lastDoc } = await getLobbyRooms(lastRoomDoc);
    set((state) => ({
      rooms: [...state.rooms, ...newRooms],
      lastRoomDoc: lastDoc,
    }));
  },

  clearMatch: () =>
    set({
      matchData: null,
      matchLegacyData: null,
      matchSummary: null,
      matchPlayersById: {},
      matchRecentMovesById: {},
    }),
  clearRooms: () => set({ rooms: [], lastRoomDoc: null }),
}));
